import { useEffect, useRef } from 'react'
import { useSettings } from '@/context/settings-hooks'
import { useAccountStore } from '@/stores/account-store'
import { useUpdateUserMutation } from '@/services/api/users-api'

/**
 * Two-way sync of the active theme id with the user's server profile
 * (`themePreference`), so a chosen theme follows the user across devices.
 * localStorage stays the fast local cache in SettingsContext; this layers the
 * server on top.
 *
 * - On first profile load: apply the server preference (server wins on login).
 * - On later local changes: push the new theme id up (deduped).
 * - A user with no saved preference triggers NO write on login — `lastSynced`
 *   seeds to the current theme, so only a genuine change syncs up.
 *
 * Mount once inside the authenticated tree (see AppLayout).
 */
export function useThemeServerSync(): void {
  const { themeId, selectTheme } = useSettings()
  const user = useAccountStore((s) => s.user)
  const [updateUser] = useUpdateUserMutation()
  const initialized = useRef(false)
  const lastSynced = useRef<string | null>(null)

  useEffect(() => {
    if (!user) return

    // First profile load: apply the server preference; seed lastSynced so a
    // user with no saved value doesn't trigger a write. Return BEFORE the push
    // path — selectTheme re-runs this effect with the applied themeId, and this
    // commit's themeId is still stale.
    if (!initialized.current) {
      initialized.current = true
      lastSynced.current = user.themePreference ?? themeId
      if (user.themePreference && user.themePreference !== themeId) {
        selectTheme(user.themePreference)
      }
      return
    }

    // Later local changes: push the new theme id up (deduped).
    if (themeId && themeId !== lastSynced.current) {
      lastSynced.current = themeId
      void updateUser({ themePreference: themeId })
    }
  }, [user, themeId, selectTheme, updateUser])
}
